// src/components/GoalCard.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { formatDate, formatScore } from '../utils';
import { SingleDatePicker } from './FilterControls';
import { ScoreTooltip } from './ScoreTooltips';
import { useStaffContext } from '../contexts/StaffContext';

const GoalCard = ({ 
    goal, 
    clientId, 
    onClick,
    editable = false,
    statusOptions = [],
    onStatusChange,
    onTargetDateChange,
    'data-testid': dataTestId
}) => {
    const navigate = useNavigate();
    const { getDriDisplayName } = useStaffContext();
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const statusRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusRef.current && !statusRef.current.contains(event.target)) {
                setShowStatusDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    const handleClick = (e) => {
        // Don't navigate if clicking on editable elements
        if (e.target.closest('.editable-control')) return;
        
        if (onClick) {
            onClick(goal);
        } else if ((goal.goalId || goal.id) && clientId) {
            navigate(`${ROUTES.ACCOUNTS}/${clientId}/goals/${goal.goalId || goal.id}`);
        }
    };

    const handleStatusChange = async (newStatus) => {
        if (onStatusChange && newStatus !== status) {
            setIsUpdating(true);
            try {
                await onStatusChange(goal.goalId || goal.id, newStatus);
            } finally {
                setIsUpdating(false);
                setShowStatusDropdown(false);
            }
        } else {
            setShowStatusDropdown(false);
        }
    };

    // Get status badge color
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'open':
            case 'in progress':
            case 'in-progress':
                return { bg: 'rgba(110, 79, 196, 0.1)', color: 'var(--pallium-primary-purple)' };
            case 'on hold':
            case 'on-hold':
                return { bg: 'rgba(239, 163, 29, 0.1)', color: 'var(--pallium-warning-yellow)' };
            case 'completed':
            case 'closed':
                return { bg: 'rgba(19, 185, 85, 0.1)', color: 'var(--pallium-success-green)' };
            default:
                return { bg: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-mute-text)' };
        }
    };

    // Truncate description to a snippet
    const truncateText = (text, maxLength = 150) => {
        if (!text) return null;
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + '...';
    };

    // Get the name from various possible fields
    const goalName = goal.goalName || goal.name || 'Untitled Goal';
    const status = goal.goalStatus || goal.status;
    const statusStyle = getStatusColor(status);
    
    // Get blocker count from various possible fields
    const blockerCount = goal.blockingIssuesCount ?? goal.blockerCount ?? 0;
    const initiativeCount = goal.initiativeCount ?? 0;
    
    // Get DRI from various possible fields
    const dri = goal.assigned_to || goal.dri || goal.primary_staff || null;
    
    // Get department
    const department = goal.department || null;
    
    // Get description
    const description = goal.description || null;
    
    // Get target date
    const targetDate = goal.target_date || goal.targetDate || null;
    
    // Get priority/risk score
    const score = goal.score ?? goal.riskScore ?? null;

    return (
        <div
            onClick={handleClick}
            className="p-4 rounded-lg transition-all duration-200 cursor-pointer"
            data-testid={dataTestId}
            style={{
                backgroundColor: 'var(--pallium-white)',
                border: '1px solid var(--pallium-stroke-light)',
                boxShadow: 'var(--pallium-shadow-sm)',
                opacity: isUpdating ? 0.7 : 1
            }}
            onMouseOver={(e) => {
                e.currentTarget.style.boxShadow = 'var(--pallium-shadow-md)';
                e.currentTarget.style.borderColor = 'var(--pallium-primary-purple)';
            }}
            onMouseOut={(e) => {
                e.currentTarget.style.boxShadow = 'var(--pallium-shadow-sm)';
                e.currentTarget.style.borderColor = 'var(--pallium-stroke-light)';
            }}
        >
            {/* Header: Icon + Goal Name + Status/Score */}
            <div className="flex items-start gap-3 mb-2">
                {/* Bullseye Icon */}
                <div 
                    className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: 'var(--pallium-purple-1)', color: 'var(--pallium-primary-purple)' }}
                >
                    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <circle cx="12" cy="12" r="6" />
                        <circle cx="12" cy="12" r="2" />
                    </svg>
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm leading-tight" style={{ color: 'var(--pallium-text-primary)' }}>
                        {goalName}
                    </h4>
                    {/* Status - Editable or Static */}
                    {editable && statusOptions.length > 0 ? (
                        <div className="relative editable-control" ref={statusRef}>
                            <button
                                onClick={(e) => { e.stopPropagation(); setShowStatusDropdown(!showStatusDropdown); }}
                                className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs font-medium transition-all"
                                style={{ 
                                    backgroundColor: statusStyle.bg, 
                                    color: statusStyle.color,
                                    border: '1px dashed currentColor'
                                }}
                                title="Click to change status"
                            >
                                {status || 'Set Status'}
                                <svg className="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                            {showStatusDropdown && (
                                <div 
                                    className="absolute left-0 top-full mt-1 z-50 rounded-lg shadow-lg py-1 min-w-[120px]"
                                    style={{ backgroundColor: 'var(--pallium-white)', border: '1px solid var(--pallium-stroke-light)' }}
                                >
                                    {statusOptions.map(opt => (
                                        <button
                                            key={opt}
                                            onClick={(e) => { e.stopPropagation(); handleStatusChange(opt); }}
                                            className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors"
                                            style={{ 
                                                color: opt === status ? 'var(--pallium-primary-purple)' : 'var(--pallium-text-primary)',
                                                fontWeight: opt === status ? 600 : 400
                                            }}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : status && (
                        <span 
                            className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium"
                            style={{ backgroundColor: statusStyle.bg, color: statusStyle.color }}
                        >
                            {status}
                        </span>
                    )}
                </div>
                {/* Priority Score */}
                {score !== null && (
                    <div className="text-right flex-shrink-0">
                        <div className="text-xs flex items-center justify-end gap-0.5" style={{ color: 'var(--pallium-text-tertiary)' }}>
                            Priority
                            <ScoreTooltip type="goalPriority" size="sm" />
                        </div>
                        <div className="text-sm font-bold" style={{ color: 'var(--pallium-primary-purple)' }}>
                            {formatScore ? formatScore(score) : (typeof score === 'number' ? score.toFixed(1) : score)}
                        </div>
                    </div>
                )}
            </div>

            {/* Description snippet */}
            {description && (
                <p className="text-xs mb-2 leading-relaxed" style={{ color: 'var(--pallium-text-secondary)' }}>
                    {truncateText(description)}
                </p>
            )}

            {/* Department and DRI */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-2" style={{ color: 'var(--pallium-text-secondary)' }}>
                {department && (
                    <div>
                        <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>Dept:</span>
                        <span className="ml-1">{department}</span>
                    </div>
                )}
                {dri && (
                    <div>
                        <span className="font-medium" style={{ color: 'var(--pallium-text-tertiary)' }}>DRI:</span>
                        <span className="ml-1">{getDriDisplayName(dri)}</span>
                    </div>
                )}
            </div>

            {/* Footer: Blockers count + Target Date */}
            <div className="flex items-center justify-between text-xs pt-2" style={{ borderTop: '1px solid var(--pallium-stroke-lighter)' }}>
                <div className="flex items-center gap-3">
                    {initiativeCount > 0 && (
                        <div style={{ color: 'var(--pallium-text-secondary)' }}>
                            <span className="font-medium">Initiatives:</span> {initiativeCount}
                        </div>
                    )}
                    <div style={{ color: blockerCount > 0 ? 'var(--pallium-error-red)' : 'var(--pallium-text-secondary)' }}>
                        <span className="font-medium">Blockers:</span> {blockerCount}
                    </div>
                </div>
                {/* Target Date - Editable or Static */}
                {editable ? (
                    <div className="editable-control flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <span className="font-medium text-xs" style={{ color: 'var(--pallium-text-tertiary)' }}>Target:</span>
                        <SingleDatePicker
                            value={targetDate ? targetDate.split('T')[0] : ''}
                            onChange={(newDate) => {
                                if (onTargetDateChange) {
                                    setIsUpdating(true);
                                    onTargetDateChange(goal.goalId || goal.id, newDate).finally(() => setIsUpdating(false));
                                }
                            }}
                            align="right"
                        />
                    </div>
                ) : targetDate && (
                    <div style={{ color: 'var(--pallium-text-tertiary)' }}>
                        <span className="font-medium">Target:</span> {formatDate ? formatDate(targetDate) : targetDate}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GoalCard;

